import React, { useState, useRef, useEffect } from 'react';
import './MultiSelectDropdown.css';

export default function MultiSelectDropdown({
    options = [],
    valueKey = 'id',
    labelKey = 'name',
    selected = [],
    onChange = () => { },
    placeholder = 'Select areas',
    maxVisibleTags = 1,
}) {
    const [open, setOpen] = useState(false);
    const [filter, setFilter] = useState('');
    const containerRef = useRef(null);
    const inputRef = useRef(null);

    // Defensive normalization: ensure arrays
    const safeOptions = Array.isArray(options) ? options : [];
    const safeSelected = Array.isArray(selected) ? selected : [];

    useEffect(() => {
        function handleDocClick(e) {
            if (!containerRef.current?.contains(e.target)) {
                setOpen(false);
            }
        }
        function handleEsc(e) {
            if (e.key === 'Escape') setOpen(false);
        }
        document.addEventListener('mousedown', handleDocClick);
        document.addEventListener('keydown', handleEsc);
        return () => {
            document.removeEventListener('mousedown', handleDocClick);
            document.removeEventListener('keydown', handleEsc);
        };
    }, []);

    useEffect(() => {
        if (open && inputRef.current) {
            inputRef.current.focus();
        }
    }, [open]);

    const isSelected = (opt) =>
        safeSelected.some((s) => String(s[valueKey]) === String(opt[valueKey]));

    const toggleOption = (opt) => {
        if (isSelected(opt)) {
            onChange(safeSelected.filter((s) => String(s[valueKey]) !== String(opt[valueKey])));
        } else {
            onChange([...safeSelected, opt]);
        }
    };

    const removeTag = (tag, e) => {
        e.stopPropagation();
        onChange(safeSelected.filter((s) => String(s[valueKey]) !== String(tag[valueKey])));
    };

    const clearAll = (e) => {
        e.stopPropagation();
        onChange([]);
    };

    const visibleTags = safeSelected.slice(0, Math.max(0, maxVisibleTags));
    const hiddenCount = Math.max(0, safeSelected.length - visibleTags.length);

    const filteredOptions = safeOptions.filter((opt) =>
        String(opt[labelKey]).toLowerCase().includes(String(filter).toLowerCase())
    );

    // SELECT ALL logic: operates on currently filteredOptions (visible)
    const visibleOptionIds = filteredOptions.map((o) => String(o[valueKey]));
    const selectedVisibleCount = filteredOptions.filter((o) => isSelected(o)).length;
    const isAllVisibleSelected = filteredOptions.length > 0 && selectedVisibleCount === filteredOptions.length;
    const isSomeVisibleSelected = selectedVisibleCount > 0 && selectedVisibleCount < filteredOptions.length;

    const toggleSelectAllVisible = (e) => {
        e.stopPropagation();
        if (isAllVisibleSelected) {
            // unselect visible options
            const newSelected = safeSelected.filter(
                (s) => !visibleOptionIds.includes(String(s[valueKey]))
            );
            onChange(newSelected);
        } else {
            // add all visible options (avoid duplicates)
            const toAdd = filteredOptions.filter(
                (o) => !safeSelected.some((s) => String(s[valueKey]) === String(o[valueKey]))
            );
            onChange([...safeSelected, ...toAdd]);
        }
    };

    return (
        <div className="msd-container" ref={containerRef}>
            <div className='msd-floting-lable'>
                {placeholder}
            </div>
            <button
                type="button"
                className={`msd-control ${open ? 'open' : ''}`}
                onClick={() => setOpen((o) => !o)}
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                <div className="msd-values" aria-hidden={open}>
                    {safeSelected.length === 0 ? (
                        <span className="msd-placeholder">{placeholder}</span>
                    ) : (
                        <>
                            {visibleTags.map((tag) => (
                                <span key={String(tag[valueKey])} className="msd-tag">
                                    <span className="msd-tag-label">{tag[labelKey]}</span>
                                    <button
                                        type="button"
                                        className="msd-tag-remove"
                                        onClick={(e) => removeTag(tag, e)}
                                        aria-label={`Remove ${tag[labelKey]}`}
                                    >
                                        ×
                                    </button>
                                </span>
                            ))}
                            {hiddenCount > 0 && <span className="msd-more">+{hiddenCount} more</span>}
                        </>
                    )}
                </div>

                <div className="msd-actions">
                    {safeSelected.length > 0 && (
                        <button
                            type="button"
                            className="msd-clear"
                            onClick={clearAll}
                            aria-label="Clear selections"
                        >
                            Clear
                        </button>
                    )}
                    <span className="msd-caret" aria-hidden>
                        ▾
                    </span>
                </div>
            </button>

            {open && (
                <div className="msd-dropdown" role="dialog" aria-modal="false">
                    <input
                        ref={inputRef}
                        className="msd-filter"
                        placeholder="Search..."
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        aria-label="Filter options"
                    />

                    <ul role="listbox" className="msd-list" aria-multiselectable="true">
                        {/* Select All row */}
                        <li
                            key="__select_all__"
                            className={`msd-item msd-select-all ${isAllVisibleSelected ? 'selected' : ''} ${isSomeVisibleSelected ? 'partial' : ''}`}
                            onClick={toggleSelectAllVisible}
                            role="option"
                            aria-selected={isAllVisibleSelected}
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    toggleSelectAllVisible(e);
                                }
                            }}
                        >
                            <input
                                type="checkbox"
                                readOnly
                                tabIndex={-1}
                                checked={isAllVisibleSelected}
                                aria-hidden
                            />
                            <span className="msd-item-label">Select All</span>
                        </li>

                        {filteredOptions.length === 0 && <li className="msd-empty">No results</li>}

                        {filteredOptions.map((opt) => {
                            const selectedForOpt = isSelected(opt);
                            return (
                                <li
                                    key={String(opt[valueKey])}
                                    className={`msd-item ${selectedForOpt ? 'selected' : ''}`}
                                    onClick={() => toggleOption(opt)}
                                    role="option"
                                    aria-selected={selectedForOpt}
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            toggleOption(opt);
                                        }
                                    }}
                                >
                                    <input
                                        type="checkbox"
                                        readOnly
                                        tabIndex={-1}
                                        checked={selectedForOpt}
                                        aria-hidden
                                    />
                                    <span className="msd-item-label">{opt[labelKey]}</span>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}
        </div>
    );
}
